import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Menu, X, Moon, Sun } from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Inicio', href: 'hero' },
  { label: 'Propiedades', href: 'properties' },
  { label: 'Nosotros', href: 'about' },
  { label: 'Testimonios', href: 'testimonials' },
  { label: 'Contacto', href: 'contact' },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Manejar el scroll para cambiar el estilo del header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Manejar el cambio de tema (claro/oscuro)
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white dark:bg-dark shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <h1 className="text-2xl font-serif font-bold text-primary dark:text-white">
            LuxuryEstates
          </h1>
        </div>

        {/* Navegación Desktop */}
        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map((item) => (
            <ScrollLink
              key={item.href}
              to={item.href}
              spy={true}
              smooth={true}
              offset={-100}
              duration={500}
              className="text-dark dark:text-light hover:text-primary dark:hover:text-secondary cursor-pointer font-medium transition-colors"
            >
              {item.label}
            </ScrollLink>
          ))}
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button className="btn btn-primary">Contactar</button>
        </nav>

        {/* Botón de menú móvil */}
        <div className="md:hidden flex items-center space-x-4">
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Menú móvil */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white dark:bg-dark shadow-lg py-4">
            <nav className="container flex flex-col space-y-4">
              {navItems.map((item) => (
                <ScrollLink
                  key={item.href}
                  to={item.href}
                  spy={true}
                  smooth={true}
                  offset={-100}
                  duration={500}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-dark dark:text-light hover:text-primary dark:hover:text-secondary py-2 font-medium transition-colors"
                >
                  {item.label}
                </ScrollLink>
              ))}
              <button className="btn btn-primary w-full">Contactar</button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;